import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Encounter, LabRequest, RadiologyRequest } from '../../types';
import { Search, FileText, FlaskConical, Scan, Printer, Download, X } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
import jsPDF from 'jspdf';
interface Props {}
export const PatientReports: React.FC<Props> = () => {
  const {
    encounters,
    patients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewing, setViewing] = useState<{
    encounter: Encounter;
  } | null>(null);
  // Encounters that have at least one completed lab OR a radiology report
  const reportEncounters = useMemo(() => {
    return encounters.filter(e => {
      const hasCompletedLab = e.labRequests.some(l => l.status === 'Completed');
      const hasRadiologyReport = e.radiologyRequests.some(r => r.report || r.images && r.images.length > 0);
      return hasCompletedLab || hasRadiologyReport;
    }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [encounters]);
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reportEncounters;
    return reportEncounters.filter(e => {
      const p = patients.find(pt => pt.id === e.patientId);
      if (!p) return false;
      return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
    });
  }, [reportEncounters, patients, searchQuery]);
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const handlePrint = () => window.print();
  const handleDownloadPDF = () => {
    if (!viewing) return;
    const patient = getPatient(viewing.encounter.patientId);
    if (!patient) return;
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4'
    });
    const margin = 40;
    let y = margin;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient Report', margin, y);
    y += 22;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${patient.firstName} ${patient.lastName} · BMC ${patient.bmcFileNumber} · ${patient.age} yrs · ${patient.sex}`, margin, y);
    y += 14;
    pdf.text(`Visit date: ${new Date(viewing.encounter.startedAt).toLocaleString()}`, margin, y);
    y += 20;
    const lineCheck = () => {
      const ph = pdf.internal.pageSize.getHeight() - margin;
      if (y > ph) {
        pdf.addPage();
        y = margin;
      }
    };
    // ── Laboratory results ──
    const completedLabs = viewing.encounter.labRequests.filter(l => l.status === 'Completed');
    if (completedLabs.length > 0) {
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Laboratory Results', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      completedLabs.forEach(lab => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
        pdf.text(`Tests: ${activeTests.join(', ')}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (lab.completedAt) {
          pdf.text(`Completed: ${new Date(lab.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        if (lab.results) {
          Object.entries(lab.results).forEach(([k, v]) => {
            if (!v) return;
            lineCheck();
            const line = `  ${k}: ${v}`;
            const lines = pdf.splitTextToSize(line, 515);
            pdf.text(lines, margin, y);
            y += lines.length * 11;
          });
        }
        if (lab.otherTests) {
          lineCheck();
          pdf.text(`  Other: ${lab.otherTests}`, margin, y);
          y += 12;
        }
        if (lab.labComments) {
          lineCheck();
          pdf.setFont('helvetica', 'bold');
          pdf.text('Comments / Observations:', margin, y);
          y += 12;
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(lab.labComments, 515);
          lines.forEach((line: string) => {
            lineCheck();
            pdf.text(line, margin, y);
            y += 11;
          });
        }
        y += 6;
      });
    }
    // ── Radiology results ──
    const radWithReport = viewing.encounter.radiologyRequests.filter(r => r.report);
    if (radWithReport.length > 0) {
      lineCheck();
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Radiology Reports', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      radWithReport.forEach(r => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Scan: ${r.scanType}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (r.completedAt) {
          pdf.text(`Completed: ${new Date(r.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        const lines = pdf.splitTextToSize(r.report || '', 515);
        lines.forEach((line: string) => {
          lineCheck();
          pdf.text(line, margin, y);
          y += 11;
        });
        y += 8;
      });
    }
    pdf.save(`report-${patient.bmcFileNumber}-${new Date(viewing.encounter.startedAt).toISOString().slice(0, 10)}.pdf`);
  };
  // ── Renderers for the View modal ──
  const renderLabResults = (lab: LabRequest) => {
    if (!lab.results) return null;
    const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
    const focusedTest = activeTests[0];
    if (isFBCTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5627" data-mp="3m7ci6ln9lj4n">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5628" data-mp="3m484mxnwtb1b">
            FBC Report
          </h4>
          {fbcStructure.map(cat => <div key={cat.category} className="mb-3" data-id="element-5629" data-mp="ht5xw5kzgk0q">
              <p className="font-semibold text-gray-700 mb-1" data-id="element-5630" data-mp="dqhlxp0z2wzj">{cat.category}</p>
              <table className="w-full text-xs border border-gray-300" data-id="element-5631" data-mp="dslzkyoobad0">
                <thead className="bg-gray-100" data-id="element-5632" data-mp="8qmw7osxodg2">
                  <tr data-id="element-5633" data-mp="2ygqjvm32bd7u">
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5634" data-mp="2yh0a9as0rme2">
                      Test
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-24" data-id="element-5635" data-mp="2tguaf7blitkw">
                      Result
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5636" data-mp="18xrzyg3jdryq">
                      Reference
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-20" data-id="element-5637" data-mp="1hzrkhidrfe6f">
                      Unit
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-5638" data-mp="1rq2u9lk4khl8">
                  {cat.tests.map(t => <tr key={t.name} data-id="element-5639" data-mp="1rpuglrg6h88z">
                      <td className="border border-gray-300 p-1.5" data-id="element-5640" data-mp="1woxgyyl7qqam">{t.name}</td>
                      <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5641" data-mp="1wr0b9utef5l5">
                        {lab.results![t.name] || '—'}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5642" data-mp="25oke386wt9xv">
                        {t.range}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5643" data-mp="7cltxvkpmkzo">
                        {t.unit}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>)}
        </div>;
    }
    if (isUrinalysisTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5644" data-mp="16uoqn0vw7bt">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5645" data-mp="174dy8e3uagg">
            Urinalysis Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5646" data-mp="35m48w0iguj7s">
            <thead className="bg-gray-100" data-id="element-5647" data-mp="35me17uqzh2gn">
              <tr data-id="element-5648" data-mp="30naum5vcuo6k">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5649" data-mp="30nswbi0sdulv">
                  Parameter
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-32" data-id="element-5650" data-mp="3eodj0p9toakn">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5651" data-mp="39ptx2ov8aysa">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5652" data-mp="owgnxah9amjt">
              {urinalysisTests.map(t => <tr key={t.name} data-id="element-5653" data-mp="xeqz4akczpzg">
                  <td className="border border-gray-300 p-1.5" data-id="element-5654" data-mp="xdfu67f6gc6r">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5655" data-mp="11gm0tg67c5dr">
                    {lab.results![`UA_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5656" data-mp="nifl9g54es2w">
                    {t.normal || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isTestosteroneTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5657" data-mp="1l3a5ulnrfupf">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5658" data-mp="16fgr136i0w7w">
            Testosterone Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5659" data-mp="1bd8ixx758ic3">
            <thead className="bg-gray-100" data-id="element-5660" data-mp="1z44ixhlr6juf">
              <tr data-id="element-5661" data-mp="1z32wyi378cxx">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5662" data-mp="1z2jodrzfv0wk">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5663" data-mp="23vcpm5vp7r3w">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5664" data-mp="3frf24ypgtr0c">
                  Reference (M)
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5665" data-mp="36stl57zn6k1o">
                  Reference (F)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5666" data-mp="3br4wjn5l52m0">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5667" data-mp="2x174ux8g5us2">
              {testosteroneTests.map(t => <tr key={t.name} data-id="element-5668" data-mp="31vbczjtg88uq">
                  <td className="border border-gray-300 p-1.5" data-id="element-5669" data-mp="31vl1f2yui7yb">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5670" data-mp="2o46jz0kl6etd">
                    {lab.results![`TESTO_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5671" data-mp="2e3w75h2gr9eq">
                    {t.rangeMale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5672" data-mp="2j2pm7dxef8h0">
                    {t.rangeFemale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5673" data-mp="1zja6morcdzp7">
                    {t.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isAMHTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5674" data-mp="24f8ri2dgozkv">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5675" data-mp="2ig1n6qfi20pk">
            Anti Mullerian Hormone (AMH) Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5676" data-mp="2dggpskfza0ey">
            <thead className="bg-gray-100" data-id="element-5677" data-mp="2s6nvdzkvzoy8">
              <tr data-id="element-5678" data-mp="2s5cuc7itsvat">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5679" data-mp="2s7z1i61t18si">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5680" data-mp="2n7tfp6i7ugic">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5681" data-mp="314gbh5yfn0mo">
                  Reference (by age)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5682" data-mp="348xksebl6lh7">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5683" data-mp="395ecfihzvhz1">
              <tr className="align-top" data-id="element-5684" data-mp="3e6lf535a5qyu">
                <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5685" data-mp="3e5tl3wrc01ao">
                  {amhStructure.name}
                </td>
                <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5686" data-mp="3izeo637s7lgq">
                  {lab.results![amhStructure.storageKey] || focusedTest && lab.results![focusedTest] || lab.results!['AMH'] || lab.results!['Anti-Müllerian Hormone'] || lab.results!['ANTI-MÜLLERIAN HORMONE'] || lab.results!['Anti Mullerian Hormone (AMH)'] || '—'}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5687" data-mp="22kmuzo2taa6d">
                  {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-5688" data-mp="27jzpmuzxclji">
                      <span className="font-medium" data-id="element-5689" data-mp="onagvli25myi">{a.ageBand}:</span>{' '}
                      {a.range}
                    </div>)}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5690" data-mp="fuguz583vpbo">
                  {amhStructure.unit}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] italic text-gray-600" data-id="element-5691" data-mp="atsuzu1wfy5b">
            Method: {amhStructure.method}
          </p>
        </div>;
    }
    if (isHormonalProfileTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5692" data-mp="yky9o5w3ske1">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5693" data-mp="yk5d1xquzb9m">
            Hormonal Profile Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5694" data-mp="tlvc16igdgg3">
            <thead className="bg-gray-100" data-id="element-5695" data-mp="3jcgyo86mp8jh">
              <tr data-id="element-5696" data-mp="3j92tks6kyl8c">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5697" data-mp="3ecm2pq7zxsrk">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5698" data-mp="3eep416eln8bl">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5699" data-mp="1au4jb7x0gobf">
                  Reference Range
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5700" data-mp="x40zenllt6p5">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5701" data-mp="1prnccmgq1noo">
              {hormonalProfileTests.map(h => <tr key={h.key} data-id="element-5702" data-mp="1psf1pfjq6p6i">
                  <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5703" data-mp="1tsyts4pfz2ls">
                    {h.name}
                  </td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5704" data-mp="1fsecxe337gux">
                    {lab.results![`HP_${h.key}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5705" data-mp="1kuddkg7fzzo6">
                    {h.ranges.map(r => <div key={r.label} data-id="element-5706" data-mp="9d414dmqe7wd">
                        <span className="font-medium" data-id="element-5707" data-mp="9eykhnictryc">{r.label}:</span>{' '}
                        {r.value}
                      </div>)}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5708" data-mp="3sz48dlgbxb49">
                    {h.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    // Fallback: generic key/value table
    return <div className="text-sm" data-id="element-5709" data-mp="3eazvwz7vdvtx">
        <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5710" data-mp="3ec2nzagxi4bf">
          {activeTests.join(', ')} Report
        </h4>
        <table className="w-full text-xs border border-gray-300" data-id="element-5711" data-mp="3a7l7pl9s295e">
          <thead className="bg-gray-100" data-id="element-5712" data-mp="3o1uk494of5i0">
            <tr data-id="element-5713" data-mp="3o2x8a9am6tu8">
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5714" data-mp="3jamapd3cjk4u">
                Parameter
              </th>
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5715" data-mp="2vk04lrcwk9xh">Result</th>
            </tr>
          </thead>
          <tbody data-id="element-5716" data-mp="2qknkhedf1gyl">
            {Object.entries(lab.results).map(([k, v]) => v ? <tr key={k} data-id="element-5717" data-mp="uhiodlfd2ci8">
                  <td className="border border-gray-300 p-1.5" data-id="element-5718" data-mp="uiaeigq6vi14">{k}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5719" data-mp="zgc6xs9i8tp9">
                    {v}
                  </td>
                </tr> : null)}
          </tbody>
        </table>
      </div>;
  };
  const renderRadReport = (r: RadiologyRequest) => <div className="text-sm" data-id="element-5720" data-mp="bkhlc65damgx">
      <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5721" data-mp="bl96en1c91sy">
        Radiology Report — {r.scanType}
      </h4>
      {r.completedAt && <p className="text-xs text-gray-500 mb-2" data-id="element-5722" data-mp="li44x5xm3278">
          Completed: {new Date(r.completedAt).toLocaleString()}
        </p>}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-sm" data-id="element-5723" data-mp="pk8rxltk10kw">
        {r.report || 'No report available.'}
      </div>
      {r.images && r.images.length > 0 && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-5724" data-mp="20xb7qentxm40">
          {r.images.map((img, idx) => <img key={idx} src={img} alt={`${r.scanType} ${idx + 1}`} className="rounded border border-gray-200 object-cover w-full max-h-64" data-id="element-5725" data-mp="3lfdgcm0ocixo" data-mpi={idx} />)}
        </div>}
    </div>;
  return <>
      <Card data-id="element-5726" data-mp="37q1wbopwdl7h">
        <div className="flex justify-between items-center mb-4 print:hidden" data-id="element-5727" data-mp="37o7bsbijfv3x">
          <div className="flex items-center gap-2 flex-1 max-w-md" data-id="element-5728" data-mp="32qozcb5baxr6">
            <div className="relative w-full" data-id="element-5729" data-mp="32nt1snxdmb39">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5730" data-mp="2xq175m5ckavd">
                <Search className="h-4 w-4 text-gray-400" data-id="element-5731" data-mp="2tqiqx3oh0nyv" />
              </div>
              <input type="text" placeholder="Search by patient name or BMC..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-5732" data-mp="2oqxrks5e2dlm" />
            </div>
          </div>
          <Badge variant="info" className="text-sm" data-id="element-5733" data-mp="s8oat2fm8bl">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {filtered.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-5734" data-mp="3n71qq7sw5kl4">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-5735" data-mp="398trtucao97b" />
            <p className="text-lg font-medium text-gray-900" data-id="element-5736" data-mp="39ay3iygfqci5">No reports yet</p>
            <p className="text-sm" data-id="element-5737" data-mp="3dbhybvdjd414">
              Completed lab and radiology reports will appear here.
            </p>
          </div> : <ul className="divide-y divide-gray-200" data-id="element-5738" data-mp="348yx1fpeezgz">
            {filtered.map(enc => {
          const patient = getPatient(enc.patientId);
          if (!patient) return null;
          const completedLabs = enc.labRequests.filter(l => l.status === 'Completed');
          const completedRads = enc.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0);
          return <li key={enc.id} className="py-4 flex justify-between items-start gap-4" data-id="element-5739" data-mp="2wppp7qwjj8u1">
                  <div className="flex-1" data-id="element-5740" data-mp="2ryhhczauzviz">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-5741" data-mp="35qnusdmmnoeq">
                      <p className="text-base font-bold text-gray-900" data-id="element-5742" data-mp="35otan1j6k0bp">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <Badge variant="info" data-id="element-5743" data-mp="3fp3jyb23gnll">BMC: {patient.bmcFileNumber}</Badge>
                      <span className="text-xs text-gray-500" data-id="element-5744" data-mp="3ap7hwhcyj68o">
                        {patient.age} yrs · {patient.sex}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-5745" data-mp="1lcnhllcf5tm0">
                      Visit: {new Date(enc.startedAt).toLocaleString()}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-5746" data-mp="39fqeptggfivc">
                      {completedLabs.length > 0 && <span className="inline-flex items-center text-xs bg-yellow-50 text-yellow-800 px-2 py-1 rounded border border-yellow-200" data-id="element-5747" data-mp="34hoo8ngp8h9u">
                          <FlaskConical className="w-3 h-3 mr-1" data-id="element-5748" data-mp="3e788nxk28j6t" />
                          {completedLabs.length} Lab Report
                          {completedLabs.length > 1 ? 's' : ''}
                        </span>}
                      {completedRads.length > 0 && <span className="inline-flex items-center text-xs bg-purple-50 text-purple-800 px-2 py-1 rounded border border-purple-200" data-id="element-5749" data-mp="30gwe7s0068ci">
                          <Scan className="w-3 h-3 mr-1" data-id="element-5750" data-mp="2vfyw1fezteb0" />
                          {completedRads.length} Radiology Report
                          {completedRads.length > 1 ? 's' : ''}
                        </span>}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setViewing({
              encounter: enc
            })} data-id="element-5751" data-mp="i8zbrnsfzixk">
                    <FileText className="w-4 h-4 mr-2" data-id="element-5752" data-mp="db7iapc5for9" /> View Report
                  </Button>
                </li>;
        })}
          </ul>}
      </Card>

      {viewing && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-5753" data-mp="15zv9t2nhrad9">
          <div className="min-h-full flex items-start justify-center p-4" data-id="element-5754" data-mp="s8q53m59nrw2">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-5755" data-mp="s8htdxkvqsmg">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white z-10 print:hidden" data-id="element-5756" data-mp="1jzxm89d15bl4">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5757" data-mp="1orflmszen43z">
                  Patient Report
                </h3>
                <div className="flex items-center gap-2" data-id="element-5758" data-mp="1ykiilvh7itsa">
                  <Button variant="outline" onClick={handlePrint} data-id="element-5759" data-mp="1um2ol7mpkl5d">
                    <Printer className="w-4 h-4 mr-2" data-id="element-5760" data-mp="1ploe7ixmwz46" /> Print
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPDF} data-id="element-5761" data-mp="1pnrbmo149uix">
                    <Download className="w-4 h-4 mr-2" data-id="element-5762" data-mp="1kmllmxyucld3" /> Download PDF
                  </Button>
                  <button onClick={() => setViewing(null)} data-id="element-5763" data-mp="1fp1uwykyig49">
                    <X className="w-5 h-5 text-gray-400" data-id="element-5764" data-mp="1aws7pqb87u5o" />
                  </button>
                </div>
              </div>
              <div id="report-printable" className="p-6 overflow-y-auto flex-1" data-id="element-5765" data-mp="16w89skitwmih">
                {(() => {
              const patient = getPatient(viewing.encounter.patientId);
              if (!patient) return null;
              return <>
                      <div className="border-b-2 border-black pb-3 mb-4" data-id="element-5766" data-mp="rz7i2h0n0rk0">
                        <h2 className="text-xl font-bold uppercase" data-id="element-5767" data-mp="huhlyrrx0hnp">
                          Patient Report
                        </h2>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-5768" data-mp="ms15a63gzkmi">
                          <p data-id="element-5769" data-mp="rs5hhjxcht6x">
                            <span className="font-semibold" data-id="element-5770" data-mp="rt6wfzhheztx">Patient:</span>{' '}
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p data-id="element-5771" data-mp="3va6xwhgvoc8p">
                            <span className="font-semibold" data-id="element-5772" data-mp="3vayrjmqwzzwc">BMC #:</span>{' '}
                            {patient.bmcFileNumber}
                          </p>
                          <p data-id="element-5773" data-mp="91nxc3qkw5x6">
                            <span className="font-semibold" data-id="element-5774" data-mp="90mido6fyza6">Age:</span>{' '}
                            {patient.age} yrs
                          </p>
                          <p data-id="element-5775" data-mp="3cj61a038cssb">
                            <span className="font-semibold" data-id="element-5776" data-mp="3chuzg5tchv3u">Sex:</span>{' '}
                            {patient.sex}
                          </p>
                          <p className="col-span-2" data-id="element-5777" data-mp="1n1iliv12ahau">
                            <span className="font-semibold" data-id="element-5778" data-mp="1n2aerz76s2xy">Visit date:</span>{' '}
                            {new Date(viewing.encounter.startedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').map(lab => <div key={lab.id} className="mb-6" data-id="element-5779" data-mp="21r6mfv5bzdy1">
                            {renderLabResults(lab)}
                            {lab.labComments && <div className="mt-4 border border-gray-300 bg-gray-50 rounded p-3" data-id="element-5780" data-mp="2ar3g6b6kv2zy">
                                <h5 className="font-bold uppercase mb-1 text-xs" data-id="element-5781" data-mp="lc5fcqslblyc">
                                  Comments / Observations
                                </h5>
                                <pre className="whitespace-pre-wrap font-sans text-xs text-gray-800" data-id="element-5782" data-mp="gf6mscokrn29">
                                  {lab.labComments}
                                </pre>
                              </div>}
                          </div>)}

                      {viewing.encounter.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0).map(r => <div key={r.id} className="mb-6" data-id="element-5783" data-mp="2e9anlfts8bjc">
                            {renderRadReport(r)}
                          </div>)}

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').length === 0 && viewing.encounter.radiologyRequests.filter(r => r.report).length === 0 && <p className="text-gray-500 text-center py-8" data-id="element-5784" data-mp="12u9k7nirg43">
                            No reports available for this encounter.
                          </p>}
                    </>;
            })()}
              </div>
            </div>
          </div>
        </div>}

      {/* Print-specific styling */}
      <style data-id="element-5785" data-mp="3ja0knlv19vuv">{`
        @media print {
          body * { visibility: hidden; }
          #report-printable, #report-printable * { visibility: visible; }
          #report-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
        }
      `}</style>
    </>;
};